import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  Edit3,
  FolderPlus,
  FolderTree,
  Loader2,
  Lock,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/api/client";
import type { TaxonomyNode } from "@/components/taxonomy/TaxonomyCascadePicker";

const TAXONOMY_TYPE = "PNBP_DOMAIN";
const MAX_DEPTH = 6;

type Tab = "nodes" | "aspects";

interface Aspect {
  id: string;
  kode: string;
  nama: string;
  deskripsi: string | null;
  urutan: number;
  is_active: boolean;
  is_system: boolean;
  parent_id: string | null;
}

// ── Node form modal ──────────────────────────────────────────────────────────

interface NodeFormProps {
  mode: "add-child" | "add-root" | "edit";
  parent?: TaxonomyNode;
  initial?: TaxonomyNode;
  onSave: (data: { kode: string; nama: string; deskripsi?: string; urutan: number }) => void;
  onClose: () => void;
  saving?: boolean;
}

function NodeFormModal({ mode, parent, initial, onSave, onClose, saving }: NodeFormProps) {
  const [kode, setKode] = useState(initial?.kode ?? "");
  const [nama, setNama] = useState(initial?.nama ?? "");
  const [deskripsi, setDeskripsi] = useState(initial?.deskripsi ?? "");
  const [urutan, setUrutan] = useState(initial?.urutan ?? 0);

  const title =
    mode === "edit"
      ? `Edit Node — ${initial?.nama ?? ""}`
      : mode === "add-child"
        ? `Tambah anak di bawah ${parent?.nama ?? ""}`
        : "Tambah Node Root";

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div>
          <label className="block text-xs text-gray-700 mb-1">Kode (opsional)</label>
          <input
            value={kode}
            onChange={(e) => setKode(e.target.value)}
            placeholder="mis. 1.1.1.1.1"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-700 mb-1">Nama *</label>
          <input
            value={nama}
            onChange={(e) => setNama(e.target.value)}
            placeholder="mis. Bijih Nikel"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-700 mb-1">Deskripsi</label>
          <textarea
            value={deskripsi ?? ""}
            onChange={(e) => setDeskripsi(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-700 mb-1">Urutan</label>
          <input
            type="number"
            value={urutan}
            onChange={(e) => setUrutan(parseInt(e.target.value) || 0)}
            className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
          >
            Batal
          </button>
          <button
            type="button"
            disabled={!nama.trim() || saving}
            onClick={() => onSave({ kode: kode.trim(), nama: nama.trim(), deskripsi: deskripsi || undefined, urutan })}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-300"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Simpan
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Node tree row (recursive) ────────────────────────────────────────────────

interface NodeRowProps {
  node: TaxonomyNode;
  onAddChild: (parent: TaxonomyNode) => void;
  onEdit: (node: TaxonomyNode) => void;
  onDelete: (node: TaxonomyNode) => void;
  defaultOpen?: boolean;
}

function NodeRow({ node, onAddChild, onEdit, onDelete, defaultOpen }: NodeRowProps) {
  const [open, setOpen] = useState(!!defaultOpen);
  const hasChildren = !!node.children?.length;
  const canAddChild = node.level < MAX_DEPTH;

  return (
    <div>
      <div className="group flex items-center gap-1 rounded px-2 py-1 hover:bg-gray-50">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-4 text-gray-400"
          aria-label="toggle"
        >
          {hasChildren ? (
            open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
          ) : null}
        </button>
        <div className="flex-1 flex items-center gap-2 min-w-0">
          {node.kode && (
            <span className="text-xs font-mono text-gray-400 flex-shrink-0">{node.kode}</span>
          )}
          <span className="text-sm text-gray-800 truncate">{node.nama}</span>
          <span className="text-xs text-gray-400 flex-shrink-0">L{node.level}</span>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          {canAddChild && (
            <button
              type="button"
              onClick={() => onAddChild(node)}
              title="Tambah anak"
              className="rounded p-1 text-blue-600 hover:bg-blue-50"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit(node)}
            title="Edit"
            className="rounded p-1 text-gray-600 hover:bg-gray-200"
          >
            <Edit3 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(node)}
            title="Hapus"
            className="rounded p-1 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {open && hasChildren && (
        <div className="pl-4 border-l border-gray-100 ml-3">
          {node.children!.map((c) => (
            <NodeRow
              key={c.id}
              node={c}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              defaultOpen={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Aspect editor (inline list) ──────────────────────────────────────────────

function AspectEditor() {
  const queryClient = useQueryClient();
  // Semua aspek (section + sub-aspek) untuk dikelola sebagai pohon 1 level.
  const { data: aspects = [], isLoading } = useQuery<Aspect[]>({
    queryKey: ["taxonomy-aspects-full"],
    queryFn: () => api.get("/taxonomy/aspects?only_active=false"),
  });

  // Susun jadi pohon: section (parent_id null) + sub-aspeknya, terurut.
  const topLevel = useMemo(
    () => aspects.filter((a) => a.parent_id == null).sort((a, b) => a.urutan - b.urutan),
    [aspects],
  );
  const childrenByParent = useMemo(() => {
    const m = new Map<string, Aspect[]>();
    for (const a of aspects) {
      if (a.parent_id) {
        const arr = m.get(a.parent_id) ?? [];
        arr.push(a);
        m.set(a.parent_id, arr);
      }
    }
    for (const arr of m.values()) arr.sort((a, b) => a.urutan - b.urutan);
    return m;
  }, [aspects]);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{
    kode: string;
    nama: string;
    deskripsi: string;
    urutan: number;
    parent_id: string | null;
  }>({ kode: "", nama: "", deskripsi: "", urutan: 0, parent_id: null });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState({ nama: "", deskripsi: "", urutan: 0 });

  const createMut = useMutation({
    mutationFn: () => api.post("/taxonomy/aspects", draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taxonomy-aspects-full"] });
      queryClient.invalidateQueries({ queryKey: ["taxonomy-aspects"] });
      setAdding(false);
      setDraft({ kode: "", nama: "", deskripsi: "", urutan: 0, parent_id: null });
    },
  });

  const patchMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Aspect> }) =>
      api.patch(`/taxonomy/aspects/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taxonomy-aspects-full"] });
      queryClient.invalidateQueries({ queryKey: ["taxonomy-aspects"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/taxonomy/aspects/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taxonomy-aspects-full"] });
      queryClient.invalidateQueries({ queryKey: ["taxonomy-aspects"] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      alert(detail ?? "Gagal menghapus aspek");
    },
  });

  const renderRow = (a: Aspect, isChild: boolean) =>
    editingId === a.id ? (
      <div
        key={a.id}
        className={clsx("px-3 py-2 space-y-2 bg-blue-50/50", isChild && "pl-10")}
      >
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-gray-500 w-44 flex-shrink-0 truncate" title={a.kode}>{a.kode}</span>
          <input
            value={editDraft.nama}
            onChange={(e) => setEditDraft((d) => ({ ...d, nama: e.target.value }))}
            placeholder="Nama"
            className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
          <input
            type="number"
            value={editDraft.urutan}
            onChange={(e) => setEditDraft((d) => ({ ...d, urutan: Number(e.target.value) }))}
            className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
          <button
            type="button"
            disabled={!editDraft.nama || patchMut.isPending}
            onClick={() =>
              patchMut.mutate(
                {
                  id: a.id,
                  data: {
                    nama: editDraft.nama,
                    deskripsi: editDraft.deskripsi || null,
                    urutan: editDraft.urutan,
                  },
                },
                { onSuccess: () => setEditingId(null) },
              )
            }
            className="rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:text-gray-300"
            title="Simpan"
          >
            <Save className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setEditingId(null)}
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
            title="Batal"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <input
          value={editDraft.deskripsi}
          onChange={(e) => setEditDraft((d) => ({ ...d, deskripsi: e.target.value }))}
          placeholder="Deskripsi"
          className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
        />
      </div>
    ) : (
      <div
        key={a.id}
        className={clsx("px-3 py-2 flex items-center gap-3", isChild && "pl-10 bg-gray-50/40")}
      >
        {isChild && <span className="text-gray-300 -ml-4">└</span>}
        <span className="font-mono text-xs text-gray-500 w-44 flex-shrink-0 truncate" title={a.kode}>{a.kode}</span>
        <span className="flex-1 text-sm text-gray-800">
          {a.nama}
          {!isChild && (childrenByParent.get(a.id)?.length ?? 0) > 0 && (
            <span className="ml-2 text-xs text-gray-400">
              ({childrenByParent.get(a.id)!.length} sub-aspek)
            </span>
          )}
        </span>
        <span className="text-xs text-gray-400">urutan {a.urutan}</span>
        {a.is_system ? (
          <span
            className={clsx(
              "rounded-full px-2 py-0.5 text-xs",
              a.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600",
            )}
          >
            {a.is_active ? "Aktif" : "Nonaktif"}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => patchMut.mutate({ id: a.id, data: { is_active: !a.is_active } })}
            className={clsx(
              "rounded-full px-2 py-0.5 text-xs",
              a.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600",
            )}
          >
            {a.is_active ? "Aktif" : "Nonaktif"}
          </button>
        )}
        {!a.is_system && (
          <button
            type="button"
            onClick={() => {
              setEditDraft({ nama: a.nama, deskripsi: a.deskripsi ?? "", urutan: a.urutan });
              setEditingId(a.id);
            }}
            className="rounded p-1 text-blue-600 hover:bg-blue-50"
            title="Edit aspek"
          >
            <Edit3 className="h-3.5 w-3.5" />
          </button>
        )}
        {a.is_system ? (
          <span
            className="rounded p-1 text-gray-300"
            title="Aspek bawaan — tidak bisa dihapus"
          >
            <Lock className="h-3.5 w-3.5" />
          </span>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (confirm(`Hapus aspek "${a.nama}"?`)) deleteMut.mutate(a.id);
            }}
            className="rounded p-1 text-red-600 hover:bg-red-50"
            title="Hapus aspek"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Aspek aktif: Definisi/Ruang Lingkup/Subjek, Objek, Mekanisme/Tarif & Perhitungan, Dokumen.
          Tambahkan aspek baru jika diperlukan.
        </p>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Tambah Aspek
          </button>
        )}
      </div>

      {adding && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              placeholder="Kode (mis. CATATAN)"
              value={draft.kode}
              onChange={(e) => setDraft((d) => ({ ...d, kode: e.target.value.toUpperCase() }))}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              placeholder="Nama"
              value={draft.nama}
              onChange={(e) => setDraft((d) => ({ ...d, nama: e.target.value }))}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <input
            placeholder="Deskripsi"
            value={draft.deskripsi}
            onChange={(e) => setDraft((d) => ({ ...d, deskripsi: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select
              value={draft.parent_id ?? ""}
              onChange={(e) =>
                setDraft((d) => ({ ...d, parent_id: e.target.value || null }))
              }
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              title="Induk: kosong = section sendiri; pilih section = jadi sub-aspek"
            >
              <option value="">— Section sendiri (top-level)</option>
              {topLevel
                .filter((a) => a.kode !== "DOKUMEN_TERKAIT")
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    Sub-aspek dari: {a.nama}
                  </option>
                ))}
            </select>
            <input
              type="number"
              placeholder="Urutan"
              value={draft.urutan}
              onChange={(e) => setDraft((d) => ({ ...d, urutan: Number(e.target.value) }))}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Batal
            </button>
            <button
              type="button"
              disabled={!draft.kode || !draft.nama || createMut.isPending}
              onClick={() => createMut.mutate()}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-gray-300"
            >
              {createMut.isPending ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Memuat…
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
          {topLevel.length === 0 ? (
            <p className="px-3 py-4 text-sm text-gray-500">Belum ada aspek.</p>
          ) : (
            topLevel.flatMap((section) => [
              renderRow(section, false),
              ...(childrenByParent.get(section.id) ?? []).map((c) =>
                renderRow(c, true),
              ),
            ])
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function TaxonomyMasterPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("nodes");
  const [modal, setModal] = useState<NodeFormProps | null>(null);

  const { data: tree = [], isLoading } = useQuery<TaxonomyNode[]>({
    queryKey: ["taxonomy-tree", TAXONOMY_TYPE],
    queryFn: () => api.get(`/taxonomy/tree?taxonomy_type=${TAXONOMY_TYPE}`),
  });

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post("/taxonomy", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taxonomy-tree", TAXONOMY_TYPE] });
      setModal(null);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      alert(detail ?? "Gagal menambah node");
    },
  });

  const patchMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/taxonomy/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taxonomy-tree", TAXONOMY_TYPE] });
      setModal(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/taxonomy/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taxonomy-tree", TAXONOMY_TYPE] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      alert(detail ?? "Gagal menghapus node");
    },
  });

  function openAddChild(parent: TaxonomyNode) {
    setModal({
      mode: "add-child",
      parent,
      onClose: () => setModal(null),
      onSave: (data) =>
        createMut.mutate({
          taxonomy_type: TAXONOMY_TYPE,
          parent_id: parent.id,
          ...data,
        }),
    });
  }
  function openAddRoot() {
    setModal({
      mode: "add-root",
      onClose: () => setModal(null),
      onSave: (data) =>
        createMut.mutate({
          taxonomy_type: TAXONOMY_TYPE,
          parent_id: null,
          ...data,
        }),
    });
  }
  function openEdit(node: TaxonomyNode) {
    setModal({
      mode: "edit",
      initial: node,
      onClose: () => setModal(null),
      onSave: (data) => patchMut.mutate({ id: node.id, data }),
    });
  }
  function confirmDelete(node: TaxonomyNode) {
    if (node.children?.length) {
      alert("Node punya turunan. Hapus turunan dahulu.");
      return;
    }
    if (confirm(`Hapus node "${node.nama}"?`)) {
      deleteMut.mutate(node.id);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Master Taxonomy PNBP</h1>
        <p className="text-sm text-gray-500 mt-1">
          Kelola pohon klasifikasi PNBP (sampai 6 level) dan master aspek (Definisi, Ruang Lingkup, dll).
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setTab("nodes")}
          className={clsx(
            "px-4 py-2 text-sm font-medium border-b-2 -mb-px",
            tab === "nodes"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-700",
          )}
        >
          <FolderTree className="inline h-4 w-4 mr-1.5" />
          Pohon Taxonomy
        </button>
        <button
          type="button"
          onClick={() => setTab("aspects")}
          className={clsx(
            "px-4 py-2 text-sm font-medium border-b-2 -mb-px",
            tab === "aspects"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-700",
          )}
        >
          Aspek
        </button>
      </div>

      {tab === "nodes" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Maksimum 6 level kedalaman. Tombol <Plus className="inline h-3.5 w-3.5" /> menambah anak;
              hover untuk menampilkan aksi edit/hapus.
            </p>
            <button
              type="button"
              onClick={openAddRoot}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              <FolderPlus className="h-4 w-4" />
              Tambah Root
            </button>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-3 max-h-[70vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center gap-2 px-2 py-3 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Memuat…
              </div>
            ) : tree.length === 0 ? (
              <p className="px-2 py-4 text-sm text-gray-500">Belum ada node taxonomy.</p>
            ) : (
              tree.map((root) => (
                <NodeRow
                  key={root.id}
                  node={root}
                  onAddChild={openAddChild}
                  onEdit={openEdit}
                  onDelete={confirmDelete}
                  defaultOpen
                />
              ))
            )}
          </div>
        </div>
      )}

      {tab === "aspects" && <AspectEditor />}

      {modal && <NodeFormModal {...modal} saving={createMut.isPending || patchMut.isPending} />}
    </div>
  );
}
